/** Shared layout + form styles for public auth pages (login, signup, reset). */

import { APP_FONT_FAMILY } from "../constants/typography";
import { getMm } from "../theme/modernMaterialEnterprise";

export function authFieldSx(isDarkMode) {
  return {
    fontFamily: APP_FONT_FAMILY,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: getMm(isDarkMode).surfaceLowest,
      fontFamily: APP_FONT_FAMILY,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: getMm(isDarkMode).outlineVariant,
        borderWidth: "2px",
      },
      "& input, & textarea": {
        color: getMm(isDarkMode).onSurface,
        fontFamily: APP_FONT_FAMILY,
        fontSize: "0.875rem",
      },
    },
    "& .MuiInputLabel-root": {
      color: getMm(isDarkMode).onSurfaceVariant,
      fontFamily: APP_FONT_FAMILY,
      "&.Mui-focused": {
        color: getMm(isDarkMode).onSurfaceVariant,
      },
    },
    "& .MuiSelect-select": {
      color: getMm(isDarkMode).onSurface,
      fontFamily: APP_FONT_FAMILY,
      fontSize: "0.875rem",
    },
  };
}

export const authPageOuterSx = (isDarkMode) => ({
  width: "100%",
  minHeight: { xs: "100dvh", sm: "100vh" },
  // Match MUI CssBaseline body (palette.background.default) so the canvas is one color.
  backgroundColor: getMm(isDarkMode).background,
  fontFamily: APP_FONT_FAMILY,
});

/** Main auth page column — clears fixed AuthPageToggleBar + device safe areas. */
export const authPageContainerSx = {
  minHeight: { xs: "100dvh", sm: "100vh" },
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "flex-start", sm: "center" },
  alignItems: "center",
  px: { xs: 2, sm: 3 },
  pt: {
    xs: "calc(env(safe-area-inset-top, 0px) + 56px)",
    sm: "calc(env(safe-area-inset-top, 0px) + 72px)",
  },
  pb: {
    xs: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
    sm: 4,
  },
  position: "relative",
  zIndex: 1,
  boxSizing: "border-box",
};

/** Wide logo (~5.5:1) — constrain width and let height follow to avoid stretch. */
export function authLogoSx(isDarkMode) {
  return {
    display: "block",
    mx: "auto",
    width: "100%",
    maxWidth: { xs: 240, sm: 320 },
    height: "auto",
    objectFit: "contain",
    filter: isDarkMode
      ? "drop-shadow(0 4px 8px rgba(255,255,255,0.1))"
      : "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
  };
}

export const authCardShellSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
  overflow: "hidden",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  fontFamily: APP_FONT_FAMILY,
});

export const authAlertErrorSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  border: "2px solid #EF4444",
  fontFamily: APP_FONT_FAMILY,
  "& .MuiAlert-icon": { color: "#EF4444" },
  "& .MuiAlert-message": {
    color: getMm(isDarkMode).onSurface,
    fontSize: "0.875rem",
  },
});

export const authAlertWarningSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  border: "2px solid #F59E0B",
  fontFamily: APP_FONT_FAMILY,
  "& .MuiAlert-icon": { color: "#F59E0B" },
  "& .MuiAlert-message": {
    color: getMm(isDarkMode).onSurface,
    fontSize: "0.875rem",
  },
});

export const authAlertInfoSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
  fontFamily: APP_FONT_FAMILY,
  "& .MuiAlert-icon": { color: getMm(isDarkMode).onSurfaceVariant },
  "& .MuiAlert-message": {
    color: getMm(isDarkMode).onSurface,
    fontSize: "0.875rem",
  },
});

export const authAlertSuccessSx = (isDarkMode) => ({
  borderRadius: "8px",
  boxShadow: "none",
  backgroundColor: getMm(isDarkMode).surfaceLowest,
  border: "2px solid #4CAF50",
  fontFamily: APP_FONT_FAMILY,
  "& .MuiAlert-icon": { color: "#4CAF50" },
  "& .MuiAlert-message": {
    color: getMm(isDarkMode).onSurface,
    fontSize: "0.875rem",
  },
});

/** @deprecated inline duplicate — spread authFieldSx + authSelectFieldSx via authFormControlSx */
export function authFormControlSx(isDarkMode) {
  return authFieldSx(isDarkMode);
}
